/**
 * Realistic pack — exact KPI and Measure definitions extracted from live data.
 *
 * Each entry is keyed by the pack deliverable localId suffix (e.g. 'del-mrr-1')
 * and contains the KPI name, its bizRules and the Measure name with logFieldsMeta.
 *
 * These are NOT auto-generated — they were created manually in the live environment
 * and carry specific bizRule formulas (derived columns, computed rates, etc.).
 *
 * Source: benzaiten test data pack.json  →  KPIs / Measures sections.
 */

import { FieldTypes, IFieldMeta } from '@optima-stride-ai/core';
import { KPIAndMeasureCategories } from '../transaction(Measure)/measure.model';

export interface RealisticKpiDef {
    /** KPI name as it should appear in the UI */
    kpiName: string;
    /** KPI category */
    category: KPIAndMeasureCategories;
    /** Full bizRules object — exact copy from live data */
    bizRules: Record<string, unknown>;
    /** The field name in bizRules.statusFieldName used to pick the display value */
    statusFieldName: string;
    /** Unit ID string (matches Units from @optima-stride-ai/core) */
    unitId: string;
    /** Associated Measure definition */
    measure: RealisticMeasureDef;
}

export interface RealisticMeasureDef {
    /** Measure name */
    name: string;
    /** Measure category (always Progress for custom measures) */
    category: KPIAndMeasureCategories;
    /** Fields used when logging data for this measure */
    logFieldsMeta: IFieldMeta[];
}

export interface RealisticDeliverableDef {
    /** Unit ID for the deliverable value  */
    unitId: string;
    /** Expected start value for generated logs */
    startVal: number;
    /** Expected end value for generated logs */
    endVal: number;
    /** Field name from the primary KPI used to produce noisy log values */
    logValueFieldName: string;
    /** KPI definitions — all are category=Progress for the realistic pack */
    kpis: RealisticKpiDef[];
}

// ─── Shared ────────────────────────────────────────────────────────────────

/** Default auto-generated bizRules (status-only) reused for auto-created KPIs */
const AUTO_BIZ_RULES = {
    selection: [
        { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isReference: false },
        { fact: { columnName: 'status', columnAlias: 'status', isTableColumn: false }, columnType: 'number', isReference: false },
    ],
    orderBy: [{ columnAlias: 'date', orderType: 'DESC' }],
    filter: null,
    limit: null,
    statusFieldName: 'status',
};

const AUTO_LOG_FIELDS: IFieldMeta[] = [
    { name: 'status', label: 'Status', type: FieldTypes.number, isRequired: true, viewInList: true },
];

/** Helper that builds a 4-category auto-created KPI set (Progress/Perf/Quality/Innovation) */
function autoKpiSet(name: string, unitId: string): RealisticKpiDef[] {
    return [
        KPIAndMeasureCategories.Progress,
        KPIAndMeasureCategories.Performance,
        KPIAndMeasureCategories.Quality,
        KPIAndMeasureCategories.Innovation,
    ].map(category => ({
        kpiName: `Auto-generated KPI for ${name}`,
        category,
        bizRules: AUTO_BIZ_RULES,
        statusFieldName: 'status',
        unitId,
        measure: {
            name: `Auto generated Measure for ${name}`,
            category: KPIAndMeasureCategories.Progress,
            logFieldsMeta: AUTO_LOG_FIELDS,
        },
    }));
}

// ─── Per-deliverable definitions ───────────────────────────────────────────

export const REALISTIC_DELIVERABLE_DEFS: Record<string, RealisticDeliverableDef> = {

    // ── MRR Objective ──────────────────────────────────────────────────────

    'del-mrr-1': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Increase Lead Conversion Rate', '25'),
    },

    'del-mrr-2': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Increase Monthly Recurring Revenue', '25'),
    },

    // ── Customer Satisfaction Objective ───────────────────────────────────

    'del-csat-1': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Achieve Net Promoter Score (NPS) ≥ 50', '25'),
    },

    'del-csat-2': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Reduce Churn Rate', '25'),
    },

    // ── Reliability Objective ──────────────────────────────────────────────

    'del-rel-1': {
        unitId: '20', startVal: 120, endVal: 30, logValueFieldName: 'mttr',
        kpis: [{
            kpiName: 'MTTR',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'mttr',
            unitId: '20',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'mttr',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'projectname', columnAlias: 'projectname', isTableColumn: false }, columnType: 'string', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldowntime', columnAlias: 'totaldowntime', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'numberofincidents', columnAlias: 'numberofincidents', isTableColumn: false }, columnType: 'number', aggregationType: 'Count', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'mttr', columnAlias: 'mttr', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'totaldowntime','columnAlias':'totaldowntime','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~/ ~{: {'fact':{'columnName':'numberofincidents','columnAlias':'numberofincidents','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Count'}   :}~) *100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Mean Time to Recovery',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilText', name: 'projectname', type: FieldTypes.text, label: 'Project Name', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'totaldowntime', type: FieldTypes.number, label: 'Total Downtime', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'numberofincidents', type: FieldTypes.number, label: 'number_of_incidents', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'mttr', type: FieldTypes.number, label: 'Mean_Time_To_Recovery', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'Year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    'del-rel-2': {
        unitId: '25', startVal: 20, endVal: 4, logValueFieldName: 'changefailurerate2',
        kpis: [{
            kpiName: 'ChangeFailurerate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'changefailurerate2',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'changefailurerate2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'faileddeployements', columnAlias: 'faileddeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldeployements', columnAlias: 'totaldeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'changefailurerate2', columnAlias: 'changefailurerate2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'faileddeployements','columnAlias':'faileddeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/ ~{: {'fact':{'columnName':'totaldeployements','columnAlias':'totaldeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'ChangeFailureRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'faileddeployements', type: FieldTypes.number, label: 'Failed Deployements', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'totaldeployements', type: FieldTypes.number, label: 'Total Deployments', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'changefailurerate', type: FieldTypes.calculatedColumn, label: 'ChangeFailureRate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalendar', name: 'deployeddate', type: FieldTypes.date, label: 'Deployed_Date', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    // ── Bugs Objective ──────────────────────────────────────────────────────

    'del-bugs-1': {
        unitId: '20', startVal: 40, endVal: 8, logValueFieldName: 'customerreportedbugs',
        kpis: [{
            kpiName: 'Customer-Reported Bugs',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'customerreportedbugs',
            unitId: '20',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'DESC', columnAlias: 'customerreportedbugs' }],
                statusFieldName: 'customerreportedbugs',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'customerreportedbugs', columnAlias: 'customerreportedbugs', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                ],
            },
            measure: {
                name: 'Customer-Reported Bugs',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'Year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'customerreportedbugs', type: FieldTypes.number, label: 'customer_reported_bugs', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    'del-bugs-2': {
        unitId: '25', startVal: 25, endVal: 5, logValueFieldName: 'defectescaperate2',
        kpis: [{
            kpiName: 'Defect Escape Rate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'defectescaperate2',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'ASC', columnAlias: 'defectescaperate2' }],
                statusFieldName: 'defectescaperate2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'escapeddefects', columnAlias: 'escapeddefects', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldefects', columnAlias: 'totaldefects', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'defectescaperate2', columnAlias: 'defectescaperate2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'escapeddefects','columnAlias':'escapeddefects','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/ ~{: {'fact':{'columnName':'totaldefects','columnAlias':'totaldefects','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~) * 100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'DefectsEscapeRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'escapeddefects', type: FieldTypes.number, label: 'escapeddefects', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'totaldefects', type: FieldTypes.number, label: 'totaldefects', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'defectescaperate', type: FieldTypes.number, label: 'defectescaperate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilText', name: 'projectname', type: FieldTypes.text, label: 'Project Name', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    // ── Sprint Throughput Objective ────────────────────────────────────────

    'del-sprint-1': {
        // Sprint Velocity — the live deliverable was linked to Cycle Time KPI
        unitId: '25', startVal: 5, endVal: 15, logValueFieldName: 'cycletime',
        kpis: [{
            kpiName: 'Cycle Time',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'cycletime',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'DESC', columnAlias: 'date' }, { orderType: 'ASC', columnAlias: 'cycletime' }],
                statusFieldName: 'cycletime',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'cycletime', columnAlias: 'cycletime', isTableColumn: false }, value: "~{: {'fact':{'columnName':'enddate','columnAlias':'enddate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~-~{: {'fact':{'columnName':'startdate','columnAlias':'startdate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'CycleTime2',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilText', name: 'featurename', type: FieldTypes.text, label: 'Featurename', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalendar', name: 'startdate', type: FieldTypes.date, label: 'StartDate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalendar', name: 'enddate', type: FieldTypes.date, label: 'EndDate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'cycletime', type: FieldTypes.number, label: 'CycleTime', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    'del-sprint-2': {
        unitId: '25', startVal: 30, endVal: 5, logValueFieldName: 'unplannedworkrate',
        kpis: [{
            kpiName: 'Unplanned Work Rate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'unplannedworkrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'ASC', columnAlias: 'unplannedworkrate' }],
                statusFieldName: 'unplannedworkrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'unplannedstorypoints', columnAlias: 'unplannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'plannedstorypoints', columnAlias: 'plannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'total_points', columnAlias: 'total_points', isTableColumn: false }, value: "~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~+ ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'unplannedworkrate', columnAlias: 'unplannedworkrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~/(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~ + ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~))*100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Unplanned Work Rate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'unplannedstorypoints', type: FieldTypes.number, label: 'unplanned_story_points', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'plannedstorypoints', type: FieldTypes.number, label: 'planned_story_points', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'total_points', type: FieldTypes.number, label: 'total_points', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'unplannedworkrate', type: FieldTypes.number, label: 'unplanned_work_rate', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    // ── Deploy Cadence Objective ──────────────────────────────────────────

    'del-dep-1': {
        // Reduce Cycle Time — auto-generated KPIs in live data
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Reduce Cycle Time', '25'),
    },

    'del-dep-2': {
        unitId: '20', startVal: 0, endVal: 8, logValueFieldName: 'deployment',
        kpis: [{
            kpiName: 'Deployment Frequency',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'deployment',
            unitId: '20',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'deployment',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'deployment', columnAlias: 'deployment', isTableColumn: false }, columnType: 'number', aggregationType: 'Count', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                ],
            },
            measure: {
                name: 'Deployementfrequency',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'deployment', type: FieldTypes.number, label: 'Deployement', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'Year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    'del-dep-3': {
        unitId: '4', startVal: 15, endVal: 4, logValueFieldName: 'leadtime2',
        kpis: [{
            kpiName: 'Lead time for Changes',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'leadtime2',
            unitId: '4',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'leadtime2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'leadtime2', columnAlias: 'leadtime2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'productiondate','columnAlias':'productiondate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~ -~{: {'fact':{'columnName':'comitdate','columnAlias':'comitdate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~) ", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Lead Time for Changes',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { icon: 'cilCalculator', name: 'month', type: FieldTypes.number, label: 'Month', isRequired: true, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'year', type: FieldTypes.number, label: 'Year', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalculator', name: 'leadtime', type: FieldTypes.number, label: 'Leadtime', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalendar', name: 'productiondate', type: FieldTypes.date, label: 'Productiondate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                    { icon: 'cilCalendar', name: 'comitdate', type: FieldTypes.date, label: 'comitdate', isRequired: false, viewInList: true, hiddenInForm: false, isFilterable: true, isSearchable: true, validationMessages: [] },
                ],
            },
        }],
    },

    // ── Marketing Objective ───────────────────────────────────────────────

    'del-mkt-1': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Reduce Customer Acquisition Cost (CAC)', '25'),
    },

    'del-mkt-2': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Increase Marketing ROI', '25'),
    },

    // ── Digital Conversions Objective ─────────────────────────────────────

    'del-digi-1': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Achieve Website Conversion Rate ≥ 5%', '25'),
    },

    'del-digi-2': {
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Achieve Email Open Rate ≥ 30%', '25'),
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ADDITIONAL DELIVERABLES — full live-data coverage (29 obj / 54 del)
    // ═══════════════════════════════════════════════════════════════════════

    // ── Under existing obj-deploy ──────────────────────────────────────────

    'del-dep-4': {
        // Reduce Commit-to-Deploy Time (KPI: Avg Commit-to-Deploy Time, unit:3=hour)
        unitId: '3', startVal: 48, endVal: 8, logValueFieldName: 'avgcomitdeploytimehours',
        kpis: [{
            kpiName: 'Avg Commit-to-Deploy Time (hours)',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'avgcomitdeploytimehours',
            unitId: '3',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'avgcomitdeploytimehours',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'avgcomitdeploytimehours', columnAlias: 'avgcomitdeploytimehours', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'deploytimestamp','columnAlias':'deploytimestamp','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~-~{: {'fact':{'columnName':'comittimestamp','columnAlias':'comittimestamp','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~) /3600", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Avg Commit-to-Deploy Time',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'comittimestamp', label: 'Commit Timestamp', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'deploytimestamp', label: 'Deploy Timestamp', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: false, viewInList: true },
                    { name: 'avgcomitdeploytimehours', label: 'Avg Commit-to-Deploy Hours', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-dep-5': {
        // Reduce Cycle Time — same Cycle Time KPI as del-sprint-1
        unitId: '25', startVal: 20, endVal: 5, logValueFieldName: 'cycletime',
        kpis: [{
            kpiName: 'Cycle Time',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'cycletime',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'DESC', columnAlias: 'date' }, { orderType: 'ASC', columnAlias: 'cycletime' }],
                statusFieldName: 'cycletime',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'cycletime', columnAlias: 'cycletime', isTableColumn: false }, value: "~{: {'fact':{'columnName':'enddate','columnAlias':'enddate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~-~{: {'fact':{'columnName':'startdate','columnAlias':'startdate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'CycleTime2',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'featurename', label: 'Featurename', type: FieldTypes.text, isRequired: false, viewInList: true },
                    { name: 'startdate', label: 'StartDate', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'enddate', label: 'EndDate', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'cycletime', label: 'CycleTime', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    // ── Accelerate value delivery objective ───────────────────────────────

    'del-acc-dep-freq': {
        // Deployment Frequency — same KPI type as del-dep-2
        unitId: '20', startVal: 2, endVal: 12, logValueFieldName: 'deployment',
        kpis: [{
            kpiName: 'Deployment Frequency',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'deployment',
            unitId: '20',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'deployment',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'deployment', columnAlias: 'deployment', isTableColumn: false }, columnType: 'number', aggregationType: 'Count', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                ],
            },
            measure: {
                name: 'Deployementfrequency',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'deployment', label: 'Deployement', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    'del-acc-lead-time': {
        // Lead Time for Changes — same KPI type as del-dep-3
        unitId: '4', startVal: 20, endVal: 5, logValueFieldName: 'leadtime2',
        kpis: [{
            kpiName: 'Lead time for Changes',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'leadtime2',
            unitId: '4',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'leadtime2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'leadtime2', columnAlias: 'leadtime2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'productiondate','columnAlias':'productiondate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~ -~{: {'fact':{'columnName':'comitdate','columnAlias':'comitdate','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~) ", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Lead Time for Changes',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'leadtime', label: 'Leadtime', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'productiondate', label: 'Productiondate', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'comitdate', label: 'comitdate', type: FieldTypes.date, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    // ── Improve overall team output objective ─────────────────────────────

    'del-wip-1': {
        // Work In Progress (KPI: 47753, unit:20=count)
        unitId: '20', startVal: 12, endVal: 4, logValueFieldName: 'avg_of_avgwipitems',
        kpis: [{
            kpiName: 'Work In Progress',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'avg_of_avgwipitems',
            unitId: '20',
            bizRules: {
                limit: null,
                filter: { condition: [{ operator: '>', leftOperand: { fact: { columnName: 'month', tableAlias: 'ML', columnAlias: 'month' }, valueKind: 'Reference', columnType: 'number', isValueStatic: false }, rightOperand: { fact: { columnName: null, tableAlias: null }, value: '1', valueKind: 'Static', isValueStatic: true } }, { operator: '=', leftOperand: { fact: { columnName: 'year', tableAlias: 'ML', columnAlias: 'year' }, valueKind: 'Reference', columnType: 'number', isValueStatic: false }, rightOperand: { fact: { columnName: null, tableAlias: null }, value: '2026', valueKind: 'Static', isValueStatic: true } }], logicalOperator: 'and' },
                orderBy: null,
                statusFieldName: 'avg_of_avgwipitems',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'avgwipitems', columnAlias: 'avg_of_avgwipitems', isTableColumn: false }, columnType: 'number', aggregationType: 'Avg', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                    { fact: { columnName: 'projectname', columnAlias: 'projectname', isTableColumn: false }, columnType: 'string', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                ],
            },
            measure: {
                name: 'Work In Progress',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'avgwipitems', label: 'Avg WIP Items', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-sprint-vel-1': {
        // Sprint Velocity — KPI 46972 not found in export; use auto KPI set
        unitId: '20', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Achieve Sprint Velocity ≥ 10 story points/sprint', '20'),
    },

    // ── Shorten time to resolution (Engineering) objective ────────────────

    'del-bfr-2': {
        // Bug Fix Rate ≥ 90% — KPI: 47008
        unitId: '25', startVal: 60, endVal: 95, logValueFieldName: 'bugfixrate',
        kpis: [{
            kpiName: 'BugFixRate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'bugfixrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'bugfixrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugsfixed', columnAlias: 'bugsfixed', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totalbugsreported', columnAlias: 'totalbugsreported', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugfixrate', columnAlias: 'bugfixrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'bugsfixed','columnAlias':'bugsfixed','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/~{: {'fact':{'columnName':'totalbugsreported','columnAlias':'totalbugsreported','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'BugFixRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugsfixed', label: 'Bugs Fixed', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totalbugsreported', label: 'Total Bugs Reported', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugfixrate', label: 'Bug Fix Rate', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-cycle-time-1': {
        // Reduce Cycle Time — same Cycle Time KPI as del-sprint-1 / del-dep-5
        unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status',
        kpis: autoKpiSet('Reduce Cycle Time', '25'),
    },

    'del-bfr-3': {
        // Duplicate Bug Fix Rate deliverable (Q2 variant)
        unitId: '25', startVal: 60, endVal: 95, logValueFieldName: 'bugfixrate',
        kpis: [{
            kpiName: 'BugFixRate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'bugfixrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'bugfixrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugsfixed', columnAlias: 'bugsfixed', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totalbugsreported', columnAlias: 'totalbugsreported', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugfixrate', columnAlias: 'bugfixrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'bugsfixed','columnAlias':'bugsfixed','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/~{: {'fact':{'columnName':'totalbugsreported','columnAlias':'totalbugsreported','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'BugFixRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugsfixed', label: 'Bugs Fixed', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totalbugsreported', label: 'Total Bugs Reported', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugfixrate', label: 'Bug Fix Rate', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    // ── Control technical liabilities objective ───────────────────────────

    'del-tdr-1': {
        // Technical Debt Ratio (KPI: 47748, unit:25=%)
        unitId: '25', startVal: 25, endVal: 8, logValueFieldName: 'techdebtratio',
        kpis: [{
            kpiName: 'Technical Debt Ratio',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'techdebtratio',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'techdebtratio',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'techdebthours', columnAlias: 'techdebthours', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'totaldevhours', columnAlias: 'totaldevhours', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'techdebtratio', columnAlias: 'techdebtratio', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'techdebthours','columnAlias':'techdebthours','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~ /~{: {'fact':{'columnName':'totaldevhours','columnAlias':'totaldevhours','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~) *100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Technical Debt Ratio',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'techdebthours', label: 'Tech Debt Hours', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totaldevhours', label: 'Total Dev Hours', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'techdebtratio', label: 'Tech Debt Ratio', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-uwr-1': {
        // Unplanned Work Rate — same KPI as del-sprint-2
        unitId: '25', startVal: 30, endVal: 8, logValueFieldName: 'unplannedworkrate',
        kpis: [{
            kpiName: 'Unplanned Work Rate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'unplannedworkrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'ASC', columnAlias: 'unplannedworkrate' }],
                statusFieldName: 'unplannedworkrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'unplannedstorypoints', columnAlias: 'unplannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'plannedstorypoints', columnAlias: 'plannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'total_points', columnAlias: 'total_points', isTableColumn: false }, value: "~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~+ ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'unplannedworkrate', columnAlias: 'unplannedworkrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~/(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~ + ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~))*100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Unplanned Work Rate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'unplannedstorypoints', label: 'unplanned_story_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'plannedstorypoints', label: 'planned_story_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'total_points', label: 'total_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'unplannedworkrate', label: 'unplanned_work_rate', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    // ── Control accumulated technical debt objective ───────────────────────

    'del-tdr-2': {
        // Technical Debt Ratio — same KPI type as del-tdr-1
        unitId: '25', startVal: 25, endVal: 8, logValueFieldName: 'techdebtratio',
        kpis: [{
            kpiName: 'Technical Debt Ratio',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'techdebtratio',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'techdebtratio',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'techdebthours', columnAlias: 'techdebthours', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'totaldevhours', columnAlias: 'totaldevhours', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'techdebtratio', columnAlias: 'techdebtratio', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'techdebthours','columnAlias':'techdebthours','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~ /~{: {'fact':{'columnName':'totaldevhours','columnAlias':'totaldevhours','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~) *100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Technical Debt Ratio',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'techdebthours', label: 'Tech Debt Hours', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totaldevhours', label: 'Total Dev Hours', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'techdebtratio', label: 'Tech Debt Ratio', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-uwr-2': {
        // Unplanned Work Rate — same KPI type as del-uwr-1 / del-sprint-2
        unitId: '25', startVal: 30, endVal: 8, logValueFieldName: 'unplannedworkrate',
        kpis: [{
            kpiName: 'Unplanned Work Rate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'unplannedworkrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'ASC', columnAlias: 'unplannedworkrate' }],
                statusFieldName: 'unplannedworkrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'unplannedstorypoints', columnAlias: 'unplannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'plannedstorypoints', columnAlias: 'plannedstorypoints', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'total_points', columnAlias: 'total_points', isTableColumn: false }, value: "~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~+ ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'unplannedworkrate', columnAlias: 'unplannedworkrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~/(~{: {'fact':{'columnName':'unplannedstorypoints','columnAlias':'unplannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~ + ~{: {'fact':{'columnName':'plannedstorypoints','columnAlias':'plannedstorypoints','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~))*100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Unplanned Work Rate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'unplannedstorypoints', label: 'unplanned_story_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'plannedstorypoints', label: 'planned_story_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'total_points', label: 'total_points', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'unplannedworkrate', label: 'unplanned_work_rate', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    // ── Improve development workflow throughput objective ─────────────────

    'del-pr-merge': {
        // PR Merge Time (KPI: 47749, unit:3=hour)
        unitId: '3', startVal: 12, endVal: 2, logValueFieldName: 'avgprmergetimehours',
        kpis: [{
            kpiName: 'PR Merge Time',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'avgprmergetimehours',
            unitId: '3',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'avgprmergetimehours',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'projectname', columnAlias: 'projectname', isTableColumn: false }, columnType: 'string', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'avgprmergetimehours', columnAlias: 'avgprmergetimehours', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'prmerged','columnAlias':'prmerged','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~ -~{: {'fact':{'columnName':'prcreated','columnAlias':'prcreated','isTableColumn':false},'columnType':'timestamp','valueKind':'None','aggregationType':''}   :}~)/3600", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'PR Merge Time',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'avgprmergetimehours', label: 'Avg PR Merge Time (hours)', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: true, viewInList: true },
                    { name: 'prmerged', label: 'PR Merged', type: FieldTypes.date, isRequired: false, viewInList: true },
                    { name: 'prcreated', label: 'PR Created', type: FieldTypes.date, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    'del-wip-2': {
        // Work In Progress — same KPI type as del-wip-1
        unitId: '20', startVal: 12, endVal: 4, logValueFieldName: 'avg_of_avgwipitems',
        kpis: [{
            kpiName: 'Work In Progress',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'avg_of_avgwipitems',
            unitId: '20',
            bizRules: {
                limit: null,
                filter: { condition: [{ operator: '>', leftOperand: { fact: { columnName: 'month', tableAlias: 'ML', columnAlias: 'month' }, valueKind: 'Reference', columnType: 'number', isValueStatic: false }, rightOperand: { fact: { columnName: null, tableAlias: null }, value: '1', valueKind: 'Static', isValueStatic: true } }, { operator: '=', leftOperand: { fact: { columnName: 'year', tableAlias: 'ML', columnAlias: 'year' }, valueKind: 'Reference', columnType: 'number', isValueStatic: false }, rightOperand: { fact: { columnName: null, tableAlias: null }, value: '2026', valueKind: 'Static', isValueStatic: true } }], logicalOperator: 'and' },
                orderBy: null,
                statusFieldName: 'avg_of_avgwipitems',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'avgwipitems', columnAlias: 'avg_of_avgwipitems', isTableColumn: false }, columnType: 'number', aggregationType: 'Avg', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                    { fact: { columnName: 'projectname', columnAlias: 'projectname', isTableColumn: false }, columnType: 'string', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                ],
            },
            measure: {
                name: 'Work In Progress',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'avgwipitems', label: 'Avg WIP Items', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    // ── Minimize production incident impact objective ──────────────────────

    'del-mttr-2': {
        // MTTR — same KPI as del-rel-1
        unitId: '20', startVal: 120, endVal: 30, logValueFieldName: 'mttr',
        kpis: [{
            kpiName: 'MTTR',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'mttr',
            unitId: '20',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'mttr',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'projectname', columnAlias: 'projectname', isTableColumn: false }, columnType: 'string', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldowntime', columnAlias: 'totaldowntime', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'numberofincidents', columnAlias: 'numberofincidents', isTableColumn: false }, columnType: 'number', aggregationType: 'Count', isAggregatedFormula: true, isAggregationDistinct: true, isReference: true },
                    { fact: { columnName: 'mttr', columnAlias: 'mttr', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'totaldowntime','columnAlias':'totaldowntime','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Sum'}   :}~/ ~{: {'fact':{'columnName':'numberofincidents','columnAlias':'numberofincidents','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':'Count'}   :}~) *100", columnType: 'number', isAggregatedFormula: true, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Mean Time to Recovery',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: true, viewInList: true },
                    { name: 'totaldowntime', label: 'Total Downtime', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'numberofincidents', label: 'number_of_incidents', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'mttr', label: 'Mean_Time_To_Recovery', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    'del-cfr-2': {
        // Change Failure Rate — same KPI as del-rel-2
        unitId: '25', startVal: 20, endVal: 4, logValueFieldName: 'changefailurerate2',
        kpis: [{
            kpiName: 'ChangeFailurerate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'changefailurerate2',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'changefailurerate2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'faileddeployements', columnAlias: 'faileddeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldeployements', columnAlias: 'totaldeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'changefailurerate2', columnAlias: 'changefailurerate2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'faileddeployements','columnAlias':'faileddeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/ ~{: {'fact':{'columnName':'totaldeployements','columnAlias':'totaldeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'ChangeFailureRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'faileddeployements', label: 'Failed Deployements', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totaldeployements', label: 'Total Deployments', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'changefailurerate', label: 'ChangeFailureRate', type: FieldTypes.calculatedColumn, isRequired: false, viewInList: true },
                    { name: 'deployeddate', label: 'Deployed_Date', type: FieldTypes.date, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    // ── Reduce defects reaching production objective ───────────────────────

    'del-code-cov': {
        // Code Coverage (KPI: 47006, unit:25=%)
        unitId: '25', startVal: 40, endVal: 85, logValueFieldName: 'codecoverage',
        kpis: [{
            kpiName: 'Code Coverage',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'codecoverage',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'codecoverage',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'linesofcodetested', columnAlias: 'linesofcodetested', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totallinesofcode', columnAlias: 'totallinesofcode', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'codecoverage', columnAlias: 'codecoverage', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'linesofcodetested','columnAlias':'linesofcodetested','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/~{: {'fact':{'columnName':'totallinesofcode','columnAlias':'totallinesofcode','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~) *100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'Code Coverage',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'linesofcodetested', label: 'Lines of Code Tested', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totallinesofcode', label: 'Total Lines of Code', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'codecoverage', label: 'Code Coverage %', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-der-2': {
        // Defect Escape Rate — same KPI type as del-bugs-2
        unitId: '25', startVal: 25, endVal: 5, logValueFieldName: 'defectescaperate2',
        kpis: [{
            kpiName: 'Defect Escape Rate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'defectescaperate2',
            unitId: '25',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'ASC', columnAlias: 'defectescaperate2' }],
                statusFieldName: 'defectescaperate2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'escapeddefects', columnAlias: 'escapeddefects', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldefects', columnAlias: 'totaldefects', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'defectescaperate2', columnAlias: 'defectescaperate2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'escapeddefects','columnAlias':'escapeddefects','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/ ~{: {'fact':{'columnName':'totaldefects','columnAlias':'totaldefects','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~) * 100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'DefectsEscapeRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'escapeddefects', label: 'escapeddefects', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totaldefects', label: 'totaldefects', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'defectescaperate', label: 'defectescaperate', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'projectname', label: 'Project Name', type: FieldTypes.text, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    'del-bfr-1': {
        // Bug Fix Rate — same KPI type as del-bfr-2
        unitId: '25', startVal: 60, endVal: 95, logValueFieldName: 'bugfixrate',
        kpis: [{
            kpiName: 'BugFixRate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'bugfixrate',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'bugfixrate',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'month', columnAlias: 'month', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugsfixed', columnAlias: 'bugsfixed', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totalbugsreported', columnAlias: 'totalbugsreported', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'bugfixrate', columnAlias: 'bugfixrate', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'bugsfixed','columnAlias':'bugsfixed','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/~{: {'fact':{'columnName':'totalbugsreported','columnAlias':'totalbugsreported','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'BugFixRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugsfixed', label: 'Bugs Fixed', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totalbugsreported', label: 'Total Bugs Reported', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'bugfixrate', label: 'Bug Fix Rate', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    'del-bugs-3': {
        // Customer-Reported Bugs ≤ 3/month — same KPI type as del-bugs-1
        unitId: '20', startVal: 40, endVal: 3, logValueFieldName: 'customerreportedbugs',
        kpis: [{
            kpiName: 'Customer-Reported Bugs',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'customerreportedbugs',
            unitId: '20',
            bizRules: {
                limit: null, filter: null,
                orderBy: [{ orderType: 'DESC', columnAlias: 'customerreportedbugs' }],
                statusFieldName: 'customerreportedbugs',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'customerreportedbugs', columnAlias: 'customerreportedbugs', isTableColumn: false }, columnType: 'number', aggregationType: 'Sum', isAggregatedFormula: true, isAggregationDistinct: true, isReference: false },
                ],
            },
            measure: {
                name: 'Customer-Reported Bugs',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'year', label: 'Year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'customerreportedbugs', label: 'customer_reported_bugs', type: FieldTypes.number, isRequired: true, viewInList: true },
                ],
            },
        }],
    },

    // ── Reduce deployment failures objective ──────────────────────────────

    'del-cfr-3': {
        // Change Failure Rate — same KPI type as del-rel-2 / del-cfr-2
        unitId: '25', startVal: 20, endVal: 4, logValueFieldName: 'changefailurerate2',
        kpis: [{
            kpiName: 'ChangeFailurerate',
            category: KPIAndMeasureCategories.Progress,
            statusFieldName: 'changefailurerate2',
            unitId: '25',
            bizRules: {
                limit: null, filter: null, orderBy: null,
                statusFieldName: 'changefailurerate2',
                selection: [
                    { fact: { columnName: 'datetime', columnAlias: 'date', isTableColumn: true }, columnType: 'timestamp', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                    { fact: { columnName: 'faileddeployements', columnAlias: 'faileddeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'totaldeployements', columnAlias: 'totaldeployements', isTableColumn: false }, columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: true },
                    { fact: { columnName: 'changefailurerate2', columnAlias: 'changefailurerate2', isTableColumn: false }, value: "(~{: {'fact':{'columnName':'faileddeployements','columnAlias':'faileddeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~/ ~{: {'fact':{'columnName':'totaldeployements','columnAlias':'totaldeployements','isTableColumn':false},'columnType':'number','valueKind':'None','aggregationType':''}   :}~)*100", columnType: 'number', isAggregatedFormula: false, isAggregationDistinct: false, isReference: false },
                ],
            },
            measure: {
                name: 'ChangeFailureRate',
                category: KPIAndMeasureCategories.Progress,
                logFieldsMeta: [
                    { name: 'year', label: 'year', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'month', label: 'Month', type: FieldTypes.number, isRequired: false, viewInList: true },
                    { name: 'faileddeployements', label: 'Failed Deployements', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'totaldeployements', label: 'Total Deployments', type: FieldTypes.number, isRequired: true, viewInList: true },
                    { name: 'changefailurerate', label: 'ChangeFailureRate', type: FieldTypes.calculatedColumn, isRequired: false, viewInList: true },
                    { name: 'deployeddate', label: 'Deployed_Date', type: FieldTypes.date, isRequired: false, viewInList: true },
                ],
            },
        }],
    },

    // ── Auto-KPI deliverables (quad + single-auto) ────────────────────────

    'del-sales-cycle-1': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Reduce Sales Cycle Length', '25') },
    'del-pipeline-vel-1': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Increase Pipeline Velocity', '25') },
    'del-churn-rate-1': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Reduce Churn Rate per month', '25') },
    'del-clv-1': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Increase Customer Lifetime Value (CLV)', '25') },
    'del-lead-conv-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Increase Lead conversion rate', '25') },
    'del-pipeline-vel-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Increase Pipeline Velocity (Q2)', '25') },
    'del-mrr-3': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Achieve Monthly Recurring Revenue (MRR)', '25') },
    'del-cac-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Reduce Customer Acquisition Cost March - June', '25') },
    'del-mktg-roi-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Improvise Marketing ROI', '25') },
    'del-nps-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Improve Net Promoter Score', '25') },
    'del-clv-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Increase Customer Lifetime Value CLV', '25') },
    'del-churn-rate-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Reduce Churn Rate', '25') },
    'del-sales-cycle-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Reduce Sales Cycle Length ≤ 25 days', '25') },
    'del-website-conv-2': { unitId: '25', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('Achieve Website Conversion Rate ≥ 5% (digital)', '25') },
    'del-sprint-vel-2': { unitId: '20', startVal: 0, endVal: 100, logValueFieldName: 'status', kpis: autoKpiSet('SprintVelocity', '20') },
    'del-lead-time-2': { unitId: '4', startVal: 15, endVal: 2, logValueFieldName: 'status', kpis: autoKpiSet('Achieve Lead Time for Changes ≤ 2 days by Q2 2026', '4') },
};
